import { TreatmentTemplate } from 'src/API';
import {
  DryEyeCategory,
  DryEyeSeverity,
  DryEyeType
} from 'src/app/core/remote-json/treatments/dry-eye-category.model';
import { defaultTreatmentTemplateMap } from './../../../../logged-in-navbar/clinic-setup-modal/treatment-template/treatment-template-model';

export type RecommendationTemplate = Omit<TreatmentTemplate, '__typename'> & {
  showByDefault?: boolean;
};
export type RecommendationTemplateKey = string;
export type RecommendationConfidence = 'significant' | 'probable' | 'possible';

export interface TemplateRecommendation {
  key: RecommendationTemplateKey;
  reason: string;
  source: 'impressions' | 'category' | 'fallback';
  confidence?: RecommendationConfidence;
}

export function getRecommendationTemplateByKey(
  key: RecommendationTemplateKey
): RecommendationTemplate {
  const map = defaultTreatmentTemplateMap as Record<string, RecommendationTemplate>;

  const direct = map[key];
  if (direct && !(direct as any).hidden) return direct;

  const variants = Object.values(map).filter(
    t => !!t.id && isVariantOf(t.id, key) && !(t as any).hidden
  );

  if (!variants.length) {
    return { id: key, friendlyId: key, otcEyeDrops: [], rxEyeDrops: [], procedures: [], exercises: [] };
  }

  return variants.reduce((best, t) =>
    countTreatments(t) >= countTreatments(best) ? t : best
  );
}

export function recommendTemplate(
  impressions: string,
  category?: DryEyeCategory
): TemplateRecommendation {
  const fromImpressions = recommendTemplateFromImpressions(impressions);
  if (fromImpressions) return fromImpressions;

  if (category) {
    const key = toRecommendationTemplateKey(category);
    return {
      key,
      reason: `Based on selected dry eye category (${key})`,
      source: 'category',
      confidence: 'possible'
    };
  }

  return {
    key: 'moderate-mixed',
    reason: 'Default fallback',
    source: 'fallback',
    confidence: 'possible'
  };
}

export function recommendTemplateFromImpressions(
  impressions: string
): TemplateRecommendation | null {
  const list = recommendTemplatesFromImpressions(impressions);
  return list.length ? list[0] : null;
}

export function recommendTemplates(
  impressions: string,
  category?: DryEyeCategory
): TemplateRecommendation[] {
  const out: TemplateRecommendation[] = [];

  const fromImpressions = recommendTemplatesFromImpressions(impressions);
  fromImpressions.forEach(r => pushUnique(out, r));

  if (category) {
    const baseKey = toRecommendationTemplateKey(category);
    pushUnique(out, {
      key: baseKey,
      reason: 'Based on selected dry eye category',
      source: 'category',
      confidence: 'possible'
    });

    const legacyAliases: string[] = CATEGORY_LEGACY_ALIASES[baseKey] || [];
    const map = defaultTreatmentTemplateMap as Record<string, RecommendationTemplate>;
    for (const alias of legacyAliases) {
      const t = map[alias];
      if (t && !(t as any).hidden) {
        pushUnique(out, {
          key: alias,
          reason: 'Based on selected dry eye category',
          source: 'category',
          confidence: 'possible'
        });
      }
    }
  }

  if (out.length === 0) {
    out.push({
      key: 'moderate-mixed',
      reason: 'Default fallback',
      source: 'fallback',
      confidence: 'possible'
    });
  }

  return out;
}

export function recommendTemplatesFromImpressions(impressions: string): TemplateRecommendation[] {
  const text = (impressions || '').toLowerCase().trim();
  if (!text) return [];

  const matches: TemplateRecommendation[] = [];

  const rules: Array<{
    key: RecommendationTemplateKey;
    reason: string;
    all: RegExp[];
    confidence: RecommendationConfidence;
  }> = [
    {
      key: 'lagophthalmos',
      reason: 'Lagophthalmos detected in impressions',
      all: [/\blagophthalmos\b/i],
      confidence: 'significant'
    },
    {
      key: 'exposure-keratopathy',
      reason: 'Exposure Keratopathy detected in impressions',
      all: [/\bexposure\s+keratopathy\b/i],
      confidence: 'significant'
    },
    {
      key: 'trichiasis',
      reason: 'Trichiasis detected in impressions',
      all: [/\btrichiasis\b/i],
      confidence: 'probable'
    },

    {
      key: 'corneal-toxicity',
      reason: 'Toxicity detected in impressions',
      all: [/\btoxicity\b/i],
      confidence: 'significant'
    },

    {
      key: 'conjunctivochalasis-severe',
      reason: 'Conjunctival chalasis (grade 3 / severe) detected',
      all: [/\bconjunctival\s+chalasis\b/i, /\bgrade\s*3\b/i],
      confidence: 'significant'
    },
    {
      key: 'conjunctivochalasis-moderate',
      reason: 'Conjunctival chalasis (grade 2 / moderate) detected',
      all: [/\bconjunctival\s+chalasis\b/i, /\bgrade\s*2\b/i],
      confidence: 'probable'
    },
    {
      key: 'conjunctivochalasis-mild',
      reason: 'Conjunctival chalasis detected',
      all: [/\bconjunctival\s+chalasis\b/i],
      confidence: 'possible'
    },

    {
      key: 'floppy-eyelid-syndrome-severe',
      reason: 'Floppy Eyelid Syndrome (severe) detected',
      all: [/\bfloppy\s+eyelid\s+syndrome\b/i, /\bsevere\b/i],
      confidence: 'significant'
    },
    {
      key: 'floppy-eyelid-syndrome-moderate',
      reason: 'Floppy Eyelid Syndrome (moderate) detected',
      all: [/\bfloppy\s+eyelid\s+syndrome\b/i, /\bmoderate\b/i],
      confidence: 'probable'
    },
    {
      key: 'floppy-eyelid-syndrome-mild',
      reason: 'Floppy Eyelid Syndrome detected',
      all: [/\bfloppy\s+eyelid\s+syndrome\b/i],
      confidence: 'possible'
    },

    {
      key: 'slk',
      reason: 'SLK detected in impressions',
      all: [/\bslk\b/i, /\bsuperior\s+limbic\s+keratoconjunctivitis\b/i],
      confidence: 'significant'
    },

    {
      key: 'medication-induced',
      reason: 'Medication contributor detected in impressions',
      all: [/\bmedication\b/i],
      confidence: 'possible'
    },

    {
      key: 'contact-lens-induced-keratopathy',
      reason: 'Contact Lens Induced Keratopathy detected',
      all: [/\bcontact\s+lens\s+induced\s+keratopathy\b/i],
      confidence: 'probable'
    },

    {
      key: 'allergic-conjunctivitis',
      reason: 'Allergic Conjunctivitis detected',
      all: [/\ballergic\s+conjunctivitis\b/i],
      confidence: 'probable'
    },
    {
      key: 'gpc/vkc',
      reason: 'GPC/VKC detected',
      all: [/\bgpc\/vkc\b/i],
      confidence: 'probable'
    },

    {
      key: 'viral/bacterial-conjunctivitis',
      reason: 'Viral/Bacterial Conjunctivitis detected',
      all: [/\bviral\/bacterial\s+conjunctivitis\b/i],
      confidence: 'probable'
    },

    {
      key: 'demodex-blepharitis',
      reason: 'Demodex Blepharitis detected',
      all: [/\bdemodex\b/i, /\bcollarettes\b/i],
      confidence: 'probable'
    },

    {
      key: 'neurotrophic-stage-3',
      reason: 'Neurotrophic Keratitis (stage 3) detected',
      all: [/\bneurotrophic\b/i, /\bstage\s*3\b/i],
      confidence: 'significant'
    },
    {
      key: 'neurotrophic-stage-2',
      reason: 'Neurotrophic Keratitis (stage 2) detected',
      all: [/\bneurotrophic\b/i, /\bstage\s*2\b/i],
      confidence: 'significant'
    },
    {
      key: 'neurotrophic-stage-1',
      reason: 'Neurotrophic Keratitis detected',
      all: [/\bneurotrophic\b/i],
      confidence: 'probable'
    },

    {
      key: 'bacterial-blepharokeratoconjunctivitis',
      reason: 'Bacterial Blepharokeratoconjunctivitis detected',
      all: [/\bblepharokeratoconjunctivitis\b/i],
      confidence: 'probable'
    },

    {
      key: 'epithelial-basement-membrane-dystrophy/recurrent-corneal-erosion',
      reason: 'EBMD / Recurrent Corneal Erosion detected',
      all: [/\b(ebmd|epithelial\s+basement\s+membrane\s+dystrophy|recurrent\s+corneal\s+erosion|rce)\b/i],
      confidence: 'probable'
    },

    {
      key: 'corneal-neuralgia',
      reason: 'Corneal Neuralgia / Neuropathic Corneal Pain detected',
      all: [/\b(corneal\s+neuralgia|neuropathic\s+corneal\s+pain)\b/i],
      confidence: 'probable'
    },

    {
      key: 'mechanical-(entropion,distichiasis,tarsal-entropion)',
      reason: 'Mechanical lid disorder (entropion / distichiasis) detected',
      all: [/\b(entropion|distichiasis)\b/i],
      confidence: 'probable'
    },

    {
      key: 'lid-wiper-epitheliopathy',
      reason: 'Lid Wiper Epitheliopathy detected',
      all: [/\b(lid\s+wiper\s+epitheliopathy|lwe)\b/i],
      confidence: 'probable'
    },

    {
      key: 'bacterial-blepharitis',
      reason: 'Blepharitis detected',
      all: [/\bblepharitis\b/i],
      confidence: 'possible'
    }
  ];

  for (const r of rules) {
    if (!r.all.every(p => p.test(text))) continue;
    if (!templateExists(r.key)) continue;

    matches.push({
      key: r.key,
      reason: r.reason,
      source: 'impressions',
      confidence: r.confidence
    });
  }

  return matches;
}

export function toRecommendationTemplateKey(category: DryEyeCategory): RecommendationTemplateKey {
  const severity = normalizeSeverity(category && category.severity);
  const type = normalizeType(category && category.type);
  return `${severity}-${type}`;
}

function templateExists(key: RecommendationTemplateKey): boolean {
  return !!(defaultTreatmentTemplateMap as any)[key];
}

const CATEGORY_LEGACY_ALIASES: { [key: string]: string[] } = {
  'mild-aqueous':         ['aque-mild'],
  'moderate-aqueous':     ['aque-mod'],
  'severe-aqueous':       ['aque-sev'],
  'mild-mixed':           ['mix-mild'],
  'moderate-mixed':       ['mix-mod'],
  'severe-mixed':         ['mix-severe'],
  'mild-evaporative':     ['evap-mild'],
  'moderate-evaporative': ['evap-mod'],
  'severe-evaporative':   ['evap-sev']
};

function isVariantOf(templateId: string, baseKey: string): boolean {
  if (templateId === baseKey) return true;
  if (!templateId.startsWith(baseKey + '-')) return false;
  return /^\d+$/.test(templateId.slice(baseKey.length + 1));
}

function countTreatments(t: RecommendationTemplate): number {
  return (t.otcEyeDrops ? t.otcEyeDrops.length : 0)
       + (t.rxEyeDrops ? t.rxEyeDrops.length : 0)
       + (t.procedures ? t.procedures.length : 0)
       + (t.exercises ? t.exercises.length : 0);
}

function pushUnique(list: TemplateRecommendation[], incoming: TemplateRecommendation): void {
  const key = incoming && incoming.key ? String(incoming.key) : '';
  if (!key) return;

  for (let i = 0; i < list.length; i++) {
    if (String(list[i].key) === key) return;
  }

  list.push({
    key,
    reason: incoming.reason,
    source: incoming.source,
    confidence: incoming.confidence || 'possible'
  });
}

function normalizeSeverity(severity: DryEyeSeverity): 'mild' | 'moderate' | 'severe' {
  if (severity === DryEyeSeverity.Mild) return 'mild';
  if (severity === DryEyeSeverity.Severe) return 'severe';
  return 'moderate';
}

function normalizeType(type: DryEyeType): 'aqueous' | 'mixed' | 'evaporative' {
  if (type === DryEyeType.Aqueous) return 'aqueous';
  if (type === DryEyeType.Evaporative) return 'evaporative';
  return 'mixed';
}
